import qs from 'qs';

import { ServiceError } from 'src/lib/errors';
import { config } from 'src/lib/config';
import { AbstractService } from 'src/services/AbstractService';
import { COMPARE_TESTS_TIMEOUT } from 'src/constants';

class RelevanceTestsApi extends AbstractService implements Services.RelevanceTestsApi.RelevanceTests {
    public async getRelevanceTestDefinitions(
        bearerToken: string | undefined | false,
        search: App.RelevanceTestDefinitions.Search,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinitions. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            ...search,
            offset,
            limit,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definitions',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async getRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async createRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        relevanceTestDefinition: Models.IngestConfigApi.RelevanceTests.CreateRelevanceTestDefinition,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request createRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    json: {
                        ...relevanceTestDefinition,
                    },
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to create relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async updateRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        relevanceTestDefinition: Models.IngestConfigApi.RelevanceTests.UpdateRelevanceTestDefinition,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request updateRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .put(url, {
                    searchParams,
                    json: relevanceTestDefinition,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to update relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async deleteRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        signal?: AbortSignal,
    ): Promise<void> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request deleteRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            await this.api
                .delete(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<void>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to delete relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async runRelevanceTest(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        options?: {
            searchBackend?: string;
            experimentFlags?: Record<string, string>;
        },
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request runRelevanceTest. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        const requestBody = {
            searchBackend: options?.searchBackend || 'prod',
            experimentFlags: options?.experimentFlags || {},
        };

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    signal,
                    json: requestBody,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to run relevance test',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async getTestRunsForDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getTestRunsForDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            offset,
            limit,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get test runs for definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async runAllRelevanceTests(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun[]> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request runAllRelevanceTests. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun[]>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to run all relevance tests',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async exportLatestRelevanceTestRuns(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<Blob> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request exportLatestRelevanceTestRuns. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun/export`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const foo = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },

                });

            return await foo.blob();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to export latest relevance test runs',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async submitRelevanceTestRunComparison(
        bearerToken: string | undefined | false,
        experimentFlags: Record<string, string>,
        searchBackend: string,
        signal?: AbortSignal,
    ): Promise<string> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request submitTestRunComparison. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun/compare/submit`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    json: {
                        experimentFlags,
                        searchBackend,
                    },
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<string>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to submit test run comparison job',
                info: (e as Error).message,
                url,
            });
        }
    }

    /**
     * Poll for the status of a test run comparison job
     * @param bearerToken - Authentication token
     * @param jobId - The ID of the job to check
     * @param signal - Optional AbortSignal for cancellation
     * @returns Promise resolving to the job status information
     */
    public async getTestRunComparisonStatus(
        bearerToken: string | undefined | false,
        jobId: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestRunComparisonStatus> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getTestRunComparisonStatus. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun/compare/status/${jobId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const response = await this.api.get(url, {
                searchParams,
                signal,
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                },
            });

            // Check the response content type
            const contentType = response.headers.get('Content-Type');

            if (contentType === 'text/plain') {
                // If the job is finished, return the file as a Blob
                const blob = await response.blob();

                return { status: 'Finished', data: blob };
            }
            // Otherwise, parse the JSON response for job status
            return await response.json() as { status: string; errorMessage?: string };
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get test run comparison status',
                info: (e as Error).message,
                url,
            });
        }
    }

    /**
     * Submit a relevance test run comparison job and poll for its status
     * @param bearerToken - Authentication token
     * @param experimentFlags - Experiment flags for the comparison
     * @param searchBackend - Search backend to use
     * @param signal - Optional AbortSignal for cancellation
     * @returns Promise resolving to the CSV file as a Blob
     */
    public async compareRelevanceTests(
        bearerToken: string | undefined | false,
        experimentFlags: Record<string, string>,
        searchBackend: string,
        signal?: AbortSignal,
    ): Promise<Blob> {
        // Submit the job
        const jobId = await this.submitRelevanceTestRunComparison(bearerToken, experimentFlags, searchBackend, signal);

        return new Promise<Blob>((resolve, reject) => {
            const pollInterval = 10000; // 10 seconds
            const timeout = setTimeout(() => {
                reject(new Error('Comparison job timed out'));
            }, COMPARE_TESTS_TIMEOUT);

            const pollStatus: () => Promise<void> = async () => {
                try {
                    const statusData = await this.getTestRunComparisonStatus(bearerToken, jobId, signal);

                    if (statusData.status === 'Finished' && statusData.data) {
                        clearTimeout(timeout);
                        resolve(statusData.data);
                    } else if (statusData.status === 'Failed') {
                        clearTimeout(timeout);
                        reject(new Error(statusData.errorMessage || 'Job failed'));
                    } else {
                        setTimeout(pollStatus, pollInterval);
                    }
                } catch (error) {
                    clearTimeout(timeout);
                    reject(error);
                }
            };

            pollStatus();
        });
    }

    public async getRelevanceTestDefinitionIds(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<string[]> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinitionIds. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/ids`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<string[]>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definition IDs',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async compareRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        experimentFlags: Record<string, string>,
        searchBackend: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceComparisonResult> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request compareRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/compare`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    timeout: 60000,
                    json: {
                        experimentFlags,
                        searchBackend,
                    },
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceComparisonResult>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: `Failed to compare relevance test definition ${testDefinitionId}`,
                info: (e as Error).message,
                url,
            });
        }
    }
}

export const RelevanceTestsService = new RelevanceTestsApi(config.services.ingestConfigServiceApi);
